import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Pencil, Trash2 } from "lucide-react";
import { dateTimeLabel } from "../lib/clockTime";
import { counterText } from "../lib/counters";

/**
 * Issue #757 review round 2, B8 — extracted out of `PerformanceTracking.jsx`
 * unchanged (pure move, not a rewrite) so that file's own function stays
 * under the 710-line maintainability budget with the #757 extraction
 * applied first, per #749's brief item 5. Byte-identical rendering to
 * before this move; only the closure over `openEdit`/`remove` became
 * explicit props.
 *
 * Issue #110: mobile card equivalent of a table cell — a stacked
 * label/value pair so every figure the desktop table shows stays
 * reachable (nothing silently dropped) without requiring sideways scroll.
 */
export function StatCell({ label, value, accent }) {
  return (
    <div className="rounded-md bg-slate-50 px-1.5 py-1.5 min-w-0">
      <p className="text-[9px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-sm font-semibold break-words ${accent || "text-slate-900"}`}>{value}</p>
    </div>
  );
}

// Issue #110: compact card replacing a table row below `md`. Carries every
// column the desktop table has (see StatCell grid) plus both row actions.
export default function MatchCard({ m, openEdit, remove }) {
  return (
    <Card className="border-slate-200" data-testid={`match-card-${m.match_id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 truncate">{m.opponent}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {dateTimeLabel(m.match_date, m.match_time)} · {m.competition || "—"} · {m.season || "—"}
            </p>
            <p className="text-xs text-slate-500">Team: {m.team || "—"}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button size="icon" variant="ghost" className="h-11 w-11" onClick={() => openEdit(m)} data-testid={`edit-card-${m.match_id}`} title="Edit match">
              <Pencil size={16} />
            </Button>
            <Button size="icon" variant="ghost" className="h-11 w-11" onClick={() => remove(m)} data-testid={`del-card-${m.match_id}`} title="Delete match">
              <Trash2 size={16} />
            </Button>
          </div>
        </div>
        {/* #715 — the card carries every column the table does, so it
            carries the same dash. A figure that reads "not recorded" on
            desktop and "0" on a phone is the defect with a breakpoint. */}
        <div className="grid grid-cols-3 gap-1.5">
          <StatCell label="Pos" value={counterText(m.batting?.batting_position)} />
          <StatCell label="Runs" value={`${counterText(m.batting?.runs)}${m.batting?.runs != null && m.batting?.not_out ? "*" : ""}`} accent="text-[#0B2E59]" />
          <StatCell label="Balls" value={counterText(m.batting?.balls_faced)} />
          <StatCell label="4s" value={counterText(m.batting?.fours)} />
          <StatCell label="6s" value={counterText(m.batting?.sixes)} />
          <StatCell label="SR" value={counterText(m.batting?.strike_rate)} />
          <StatCell label="Wkts" value={counterText(m.bowling?.wickets)} accent="text-[#D4A017]" />
          <StatCell label="Econ" value={counterText(m.bowling?.economy_rate)} />
          <StatCell label="Dismissal" value={m.batting?.dismissal_type || "—"} />
        </div>
      </CardContent>
    </Card>
  );
}
